import { Box, Button, Typography } from "@mui/material";
import MultiSelect from "../controls/MultiSelect";
import { DashboardFilterType, RadioOption } from "../../interface";
import { useGetRefData } from "../../containers/records/useGetRefData.hook";
import styles from "./DashboardFilter.module.scss";
import { DateRangeOptions, ReviewTypeOptions } from "../../utils/constants";
import { DateRangePicker } from "rsuite";
import dayjs from "dayjs";
import { getRangeDate } from "../../utils/helper";
import { DateRange } from "rsuite/esm/DateRangePicker";

interface DashboardFilterProps {
  filter: DashboardFilterType;
  onChange: React.Dispatch<React.SetStateAction<DashboardFilterType>>;
  onApply: () => void;
}

/**
 * DashboardFilter component displays a set of dropdown filters
 * for narrowing down data on the dashboard based on various fields.
 *
 * It includes options to filter by:
 * - Date Range
 * - Region
 * - Affiliate
 * - Review Type
 * - Country
 *
 * The component retrieves reference data using `useGetRefData` and
 * dynamically builds filter options. The selected filters are stored
 * in local component state.
 *
 * @returns {JSX.Element} The rendered DashboardFilter component.
 */
const DashboardFilter: React.FC<DashboardFilterProps> = ({
  filter,
  onChange,
  onApply,
}) => {
  const { options } = useGetRefData(filter.region);
  const { distinctRegions, affiliateNames, validAffiliates } = options;

  const REGION_OPTION = distinctRegions.map(
    (item) => ({ label: item, value: item } as RadioOption)
  );

  const AFFILIATE_OPTION = affiliateNames?.map(
    (item: any) => ({ label: item, value: item } as RadioOption)
  );
  const selectConfigs = [
    {
      id: "dateRange",
      name: "dateRange",
      label: "Date range",
      value: filter.dateRange,
      options: DateRangeOptions,
      multiple: false,
      virtual: false,
      disabled: false,
    },
    {
      id: "region",
      name: "region",
      label: "Region",
      value: filter.region,
      options: REGION_OPTION,
      multiple: true,
      virtual: false,
      disabled: false,
    },
    {
      id: "affiliate",
      name: "affiliate",
      label: "Affiliate",
      value: filter.affiliate,
      options: AFFILIATE_OPTION,
      multiple: true,
      virtual: true,
      autocomplete: true,
      disabled: false,
    },
    {
      id: "reviewType",
      name: "reviewType",
      label: "Review Type",
      value: filter.reviewType,
      options: ReviewTypeOptions,
      multiple: true,
      virtual: false,
      disabled: false,
    },
    {
      id: "country",
      name: "country",
      label: "Country",
      value: filter.country,
      options: AFFILIATE_OPTION,
      multiple: true,
      virtual: true,
      autocomplete: true,
      disabled: false,
    },
  ];

  const filterChangeHandler = (selection: any) => {
    if (selection.target.name === "dateRange") {
      const [start, end] = getRangeDate(selection.target.value);
      onChange((prev: any) => ({
        ...prev,
        dateRange: selection.target.value,
        startDate: start,
        endDate: end,
        monthSelected: selection.target.value === "thisMonth",
      }));
    } else if (selection.target.name === "region") {
      onChange((prev: any) => ({
        ...prev,
        [selection.target.name]: selection.target.value,
        affiliate: validAffiliates(prev.affiliate, selection.target.value),
        country: validAffiliates(prev.country, selection.target.value),
      }));
    } else {
      onChange((prev: any) => ({
        ...prev,
        [selection.target.name]: selection.target.value,
      }));
    }
  };

  const handleCustomDateChange = (value: DateRange | null) => {
    if (!value) return;
    const [start, end] = [
      dayjs(value[0]).format("YYYY-MM-DD"),
      dayjs(value[1]).format("YYYY-MM-DD"),
    ];
    if (end < start) {
      onChange((prev: any) => ({
        ...prev,
        startDate: "",
        endDate: "",
      }));
      return;
    }
    onChange((prev: any) => ({
      ...prev,
      startDate: dayjs(start).format("YYYY-MM-DD"),
      endDate: dayjs(end).format("YYYY-MM-DD"),
    }));
  };

  return (
    <Box className={styles.filterWrapper}>
      <Typography className={styles.label}>Filter by:</Typography>
      <Box className={styles.selectContent}>
        {selectConfigs.map((config) => (
          <Box className={styles.selectContainer} key={config.id}>
            <MultiSelect
              id={config.id}
              name={config.name}
              label={config.label}
              useMuiLabel
              showLabel
              value={config.value}
              options={config.options}
              onChange={filterChangeHandler}
              multiple={config.multiple}
              isVirtualized={config.virtual}
              isAutocomplete={config.autocomplete}
              disabled={config.disabled}
            />
            {filter.dateRange === "custom" && config.name === "dateRange" && (
              <DateRangePicker
                size="lg"
                id="date-range"
                placeholder="Select Date Range"
                format="yyyy-MM-dd"
                menuStyle={{ zIndex: 9999 }}
                onChange={handleCustomDateChange}
                ranges={[]}
              />
            )}
          </Box>
        ))}
      </Box>
      <Button variant="contained" onClick={onApply}>
        Apply
      </Button>
    </Box>
  );
};

export default DashboardFilter;
